import axios, { AxiosRequestConfig, AxiosProgressEvent } from "axios";
import sysConf from "config";
import { getAwsAuthToken } from "utils/lib/authHelper";
/**
 * Uploads a file to Cloudflare.
 *
 * @param {File} file - The file to upload.
 * @param {(progress: number) => void} [onProgress] - Optional callback to track upload progress.
 * @returns {Promise<string>} - A promise that resolves to the file's download URL.
 * @throws {Error} - If the upload or URL generation fails.
 */
const API_URL = `${sysConf.apiGateway.AI_BASE_NODE_URL}/files/upload_to_image_cf`;
// const API_URL = "http://localhost:4000/files/upload_to_image_cf";


export const uploadFileToCloudflare = async (
    file: File,
    onProgress?: (progress: number) => void
): Promise<string> => {
    if (!file) {
        throw new Error("No file provided for upload.");
    }

    const formData = new FormData();
    formData.append("file", file);
    const token = await getAwsAuthToken()
    const config: AxiosRequestConfig = {
        method: "post",
        maxBodyLength: Infinity,
        url: API_URL,
        headers: {
            "Content-Type": "multipart/form-data",
            "X-Amz-Date": new Date().toISOString(),
            "x-auth-source":sysConf.isDev?"test":"prod",
            'Authorization': `Bearer ${token}`,
        },
        data: formData,
        onUploadProgress: (progressEvent: AxiosProgressEvent) => {
            if (onProgress && progressEvent.total) {
                const percentCompleted = Math.round(
                    (progressEvent.loaded * 100) / progressEvent.total
                );
                onProgress(percentCompleted);
            }
        },
    };

    try {
        const response = await axios.request(config);
        const result = response?.data;

        if (result?.error) {
            throw new Error(result.error || "Cloudflare upload error.");
        }

        const downloadURL = await generateCloudflareStreamURL(result);
        if (!downloadURL) {
            throw new Error("Failed to generate Cloudflare download URL.");
        }

        return downloadURL;
    } catch (error) {
        console.error("File upload failed:", error);
        throw error;
    }
};

interface CloudflareResponse {
    image?: {
        variants?: string[];
    };
}

/**
 * Generates the public Cloudflare Stream URL from the provided data.
 *
 * @param {CloudflareResponse} data - The response object containing image variants.
 * @returns {Promise<string | null>} - A promise that resolves to the public image URL or null if not found.
 */
export const generateCloudflareStreamURL = async (
    data: CloudflareResponse
): Promise<string | null> => {
    try {
        if (data?.image?.variants) {
            const publicUrl = data.image.variants.find((variant) =>
                variant.includes("public")
            );
            return publicUrl || null;
        }
        return null;
    } catch (error) {
        console.error("Error generating Cloudflare stream URL:", error);
        return null;
    }
};

/**
 * Extracts Cloudflare image ID from the image URL.
 *
 * @param {string} imageUrl - The Cloudflare image URL.
 * @returns {string | null} - The image ID or null if not found.
 */
const extractImageIdFromUrl = (imageUrl: string): string | null => {
    if (!imageUrl) return null;
    try {
        const match = imageUrl.match(/imagedelivery\.net\/[^/]+\/([^/]+)/);
        if (match && match[1]) {
            return match[1];
        }
        const altMatch = imageUrl.match(/\/images\/([^/?]+)/);
        if (altMatch && altMatch[1]) {
            return altMatch[1];
        }
        return null;
    } catch (error) {
        return null;
    }
};

/**
 * Deletes an image from Cloudflare.
 *
 * @param {string} imageUrl - The Cloudflare image URL to delete.
 * @returns {Promise<boolean>} - A promise that resolves to true if deletion was successful, false otherwise.
 */
export const deleteImageFromCloudflare = async (
    imageUrl: string
): Promise<boolean> => {
    if (!imageUrl) {
        console.error("No image URL provided for deletion.");
        return false;
    }

    const imageId = extractImageIdFromUrl(imageUrl);
    if (!imageId) {
        return false;
    }

    const DELETE_API_URL = `${sysConf.apiGateway.AI_BASE_NODE_URL}/files/delete_image_cf`;
    // const DELETE_API_URL = "http://localhost:4000/files/delete_image_cf";
    
    const requestData = {
        imageId: imageId,
        imageUrl: imageUrl
    };
    
    try {
        const token = await getAwsAuthToken();
        const config: AxiosRequestConfig = {
            method: "delete",
            url: DELETE_API_URL,
            headers: {
                "Content-Type": "application/json",
                "X-Amz-Date": new Date().toISOString(),
                "x-auth-source": sysConf.isDev ? "test" : "prod",
                'Authorization': `Bearer ${token}`,
            },
            data: requestData, 
        };

        const response = await axios.request(config);
        const result = response?.data;

        if (result?.error) {
            throw new Error(result.error || "Failed to delete image from Cloudflare.");
        }

        return true;
    } catch (error) {
        console.error("Failed to delete image from Cloudflare:", error);
        throw error;
    }
};
